//Libs
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

//Local
import BaseInputNumber from "./BaseInputNumber";
import { ShowRightStringForPassingMinMaxUnits } from "../assets/js/ShowRightStringForPassingMinMaxUnits";
import { ExistingChecklistResponse } from "../models/checklist-response";
import ResponseCardBase, {
  ResponseCardBaseProps,
} from "../components/ResponseCardBase";
import { logger as devLogger } from "../logging";
import { convertPassingNumberValues } from "../assets/js/PassingNumberValues";

interface Props {
  response: ExistingChecklistResponse;
  onSaveValue: (value: number | null, responseID: string) => Promise<void>;
  /**
   * Takes IconRow as children.
   */
  children: React.ReactNode;
}

export default function ResponseCardIntegerType(
  props: Props & Omit<ResponseCardBaseProps, "children">,
) {
  const ResponseIntegerSchema = z.object({
    responseIntegerValue: z.union([z.number().int(), z.literal("")]),
  });
  type ResponseIntegerState = z.infer<typeof ResponseIntegerSchema>;

  async function onSubmit(data: ResponseIntegerState) {
    if (props.response.id == null) {
      devLogger.error(`ResponseID was not found. Value: ${props.response.id}`);
      return;
    }

    if (
      data.responseIntegerValue === "" &&
      props.response.responseValue === null
    ) {
      devLogger.debug("The value hasn't changed");
      return;
    }

    if (typeof props.response.responseValue === "string") {
      if (data.responseIntegerValue === props.response.responseValue) {
        devLogger.debug("The value hasn't changed");
        return;
      }
    }

    const valueToSend = convertPassingNumberValues(data.responseIntegerValue);

    await props.onSaveValue(valueToSend, props.response.id);
    devLogger.debug("Response value was successfully recorded");
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResponseIntegerState>({
    defaultValues: {
      responseIntegerValue:
        typeof props.response.responseValue === "number"
          ? props.response.responseValue
          : "",
    },
    resolver: zodResolver(ResponseIntegerSchema),
  });

  const inputForm = (
    <form
      autoComplete="off"
      onBlur={handleSubmit(onSubmit)}
      onKeyDown={(event) =>
        event.key === "Enter" ? event.preventDefault() : null
      }
      className="mb-2 mt-3 block w-full sm:w-72"
    >
      <p className="pb-1 text-right text-xs font-medium text-greenPass">
        {ShowRightStringForPassingMinMaxUnits(props.response)}
      </p>
      <BaseInputNumber
        id={props.response.id}
        {...register("responseIntegerValue", {
          setValueAs: (value) => (value !== "" ? parseInt(value) : ""),
        })}
      />
      {errors.responseIntegerValue?.message && (
        <div className="ml-1 mt-2 text-xs text-red-400">
          {errors.responseIntegerValue?.message}
        </div>
      )}
    </form>
  );

  return (
    <ResponseCardBase response={props.response}>
      {{
        input: inputForm,
        iconRow: props.children,
      }}
    </ResponseCardBase>
  );
}
